/**
 * Strips in-app-browser (Facebook/Instagram WebView shell) performance-logger
 * noise from PostHog session-replay console capture.
 *
 * Meta's in-app browsers inject an `iabjs://navigation_performance_logger_*`
 * script (see {@link ../observability/in-app-browser-noise}) that spams the
 * page console with per-navigation performance metrics —
 * `FBNavFirstContentfulPaint`, `FBNavLargestContentfulPaint`, `FBNavINP`, … —
 * many times per session. PostHog's rrweb console plugin records every
 * `console.*` line into the replay, so in a Meta IAB session these flood the
 * replay inspector and bury the real product errors we debug from (HTTP 413s,
 * E2B boot timeouts).
 *
 * posthog-js exposes no per-message console filter (`consoleLogRecordingEnabled`
 * is all-or-nothing), and patching `console` client-side races the rrweb
 * recorder's own console patch. So we redact at the `before_send` hook instead:
 * for a `$snapshot` event we drop only the rrweb console *plugin* events whose
 * payload/trace is IAB-shell noise. Console plugin events (rrweb
 * `EventType.Plugin`) are a side channel carrying no DOM state, so removing them
 * never corrupts playback — the PostHog-documented-safe redaction surface. Every
 * other event, and every real console line, is left untouched.
 */

// rrweb `EventType.Plugin` — the event kind the console recorder emits under.
const RRWEB_PLUGIN_EVENT_TYPE = 6;
// The console plugin's stable name (rrweb-plugin-console-record `PLUGIN_NAME`).
const CONSOLE_PLUGIN_NAME = "rrweb/console@1";

// The IAB performance-logger metric names are literal CamelCase tokens
// (`FBNavFirstContentfulPaint`, `FBNavINP`, …) — matched case-SENSITIVELY so a
// stray lowercase substring (e.g. "fbnavigation") can't false-positive a real
// app log.
const FBNAV_METRIC_RE = /\bFBNav[A-Z]/;
// The injected script's pseudo-scheme / logger name — the browser-version-
// independent marker, mirrored from {@link isInAppBrowserBridgeNoise}. Appears
// in a console line's captured stack trace when the shell logs from the bridge.
const BRIDGE_SOURCE_RE = /iabjs:\/\/|navigation_performance_logger/i;

/** True when a captured console string is in-app-browser shell noise. */
function isNoiseString(s: unknown): boolean {
  return (
    typeof s === "string" &&
    (FBNAV_METRIC_RE.test(s) || BRIDGE_SOURCE_RE.test(s))
  );
}

interface RrwebEventLike {
  type?: number;
  data?: {
    plugin?: string;
    payload?: { payload?: unknown[]; trace?: unknown[] };
  };
}

function isRrwebEventLike(x: unknown): x is RrwebEventLike {
  return !!x && typeof x === "object" && typeof (x as RrwebEventLike).type === "number";
}

/**
 * True when `ev` is an rrweb console-plugin event whose logged args or captured
 * stack trace match the IAB-shell noise markers. Only console plugin events can
 * ever return true — never a DOM/mutation event.
 */
export function isInAppBrowserConsoleNoiseEvent(ev: unknown): boolean {
  if (!isRrwebEventLike(ev)) return false;
  if (ev.type !== RRWEB_PLUGIN_EVENT_TYPE) return false;
  const data = ev.data;
  if (!data || data.plugin !== CONSOLE_PLUGIN_NAME) return false;
  const log = data.payload;
  const parts = [
    ...(Array.isArray(log?.payload) ? log.payload : []),
    ...(Array.isArray(log?.trace) ? log.trace : []),
  ];
  return parts.some(isNoiseString);
}

/**
 * Filters IAB-shell console-noise events out of a `$snapshot_data` structure.
 * `$snapshot_data` is either a flat array of rrweb events or an array of
 * per-flush batches (arrays of events); this walks one level of nesting and
 * preserves the shape. Returns the (possibly new) value plus how many events
 * were dropped so the caller can skip a no-op rewrite.
 */
function scrubSnapshotData(data: unknown): { value: unknown; dropped: number } {
  if (!Array.isArray(data)) return { value: data, dropped: 0 };
  let dropped = 0;
  const out: unknown[] = [];
  for (const item of data) {
    if (isRrwebEventLike(item)) {
      if (isInAppBrowserConsoleNoiseEvent(item)) {
        dropped++;
        continue;
      }
      out.push(item);
    } else if (Array.isArray(item)) {
      const nested = scrubSnapshotData(item);
      dropped += nested.dropped;
      out.push(nested.value);
    } else {
      out.push(item);
    }
  }
  return { value: out, dropped };
}

// Minimal structural shape of posthog-js's `CaptureResult` — kept local so this
// module has no posthog-js import (trivially unit-testable, and safe to run
// before init). A real `CaptureResult` satisfies it structurally.
interface SnapshotCaptureResult {
  event?: string;
  properties?: Record<string, unknown> & { $snapshot_data?: unknown };
}

/**
 * A posthog-js `before_send` handler: drops IAB-shell console noise from
 * `$snapshot` (session-replay) events and passes everything else through
 * unchanged. Fail-safe by contract — any unexpected shape or error returns the
 * event untouched, so replay ingestion can never break because of this filter.
 */
export function stripInAppBrowserConsoleNoise<T extends SnapshotCaptureResult | null>(
  cr: T,
): T {
  if (!cr) return cr;
  try {
    if (cr.event !== "$snapshot") return cr;
    const props = cr.properties;
    const data = props?.$snapshot_data;
    if (!Array.isArray(data)) return cr;
    const { value, dropped } = scrubSnapshotData(data);
    if (dropped === 0) return cr;
    return {
      ...cr,
      properties: { ...props, $snapshot_data: value },
    };
  } catch {
    // Observability must never break the product — or, here, the recording.
    return cr;
  }
}
